import { keyframes, mergeStyles } from '@fluentui/react';
import { useTheme } from '@fluentui/react/lib/Theme';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { useTranslation } from 'react-i18next';

//
// The three dots that stand in for the answer while it is being composed. Drawn in the theme's own
// ink instead of a gif, so it follows a dark theme and a scaled font rather than staying a picture.
//
// One keyframes() rule at module scope: merge-styles registers a new rule per call, so a call inside
// the component would leak one rule per render.
//

const typingKeyframe = keyframes({
  '0%,80%,100%': { opacity: '0.25' },
  '40%': { opacity: '1' },
});

/** One full cycle of a dot. */
const typingDurationMs: number = 1200;

/** Stagger between two dots. */
const typingStaggerMs: number = 160;

const typingDotClass: string = mergeStyles({
  animationName: typingKeyframe,
  animationDuration: `${typingDurationMs}ms`,
  animationTimingFunction: 'ease-in-out',
  animationIterationCount: 'infinite',
  selectors: {
    // Standing still says the same thing as blinking: the dots are simply there, half lit.
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none', opacity: '0.6' },
  },
});

/** The dots as the chat row draws them, in the place the answer will take. */
const CopilotTypingIndicator = () => {
  const { t } = useTranslation(['copilot']);
  const theme = useTheme();

  return (
    <IPStack
      horizontal
      verticalAlign="center"
      horizontalAlign="center"
      role="status"
      aria-label={t('copilot:Chat.Typing')}
      tokens={{ childrenGap: 4 }}
      styles={{ root: { width: 40, height: 26 } }}
    >
      {[0, 1, 2].map((index) => (
        <IPText
          key={index}
          variant="large"
          className={typingDotClass}
          style={{ animationDelay: `${index * typingStaggerMs}ms`, color: theme.palette.neutralSecondary }}
        >
          {'•'}
        </IPText>
      ))}
    </IPStack>
  );
};

export default CopilotTypingIndicator;
